import { Button } from '@heidi/design-system/button';
import { toast, type ToastTone } from '@heidi/design-system/toast';
import { logger } from '@heidi/runtime/logger';
import { getCurrentSessionId } from '@heidi/runtime/shell';
import { getDefaultStore } from 'jotai';
import { Info } from 'lucide-react';
import { toast as sonnerToast } from 'sonner';

import type { UiOpenArtifactWire } from '../../../contracts/ui-open-artifact.types';
import { setOpenArtifactSilentAtom } from '../../../data/projects/artifacts/state';
import { isHelpTargetId } from '../help/helpTargets';
import {
  useVoiceGuideStore,
  type GuideStep,
  type GuideWaitFor,
} from '../help/useVoiceGuideStore';
import { useVoiceHighlightStore } from '../help/useVoiceHighlightStore';
import { applyClassicScribeOpenArtifact } from './applyClassicScribeOpenArtifact';
import {
  applyOpenWorkspaceFrame,
  applyPresentInfoFrame,
  applyWorkspaceUpdateFrame,
} from './applyWorkspaceFrames';
import type {
  VoiceUiOpenWorkspaceData,
  VoiceUiWorkspaceUpdateData,
} from './mapOpenWorkspace';
import type { VoiceUiPresentInfoData } from './mapPresentInfo';
import { openTemplatePicker } from './openTemplatePicker';
import type { VoiceUiActionsPort } from './voice-ui-actions.port';

/** Voice WS ``ui`` frame actions (aligned with bus ``ui.*`` event names). */
export type VoiceUiAction =
  | 'navigate'
  | 'open_artifact'
  | 'open_workspace'
  | 'workspace_update'
  | 'present_info'
  | 'toast'
  | 'highlight'
  | 'open_template_picker'
  | 'start_guide';

export type VoiceUiNavigateData = { href: string };

export type VoiceUiToastData = {
  variant: 'success' | 'error' | 'info';
  title?: string;
  body?: string;
  duration_ms?: number;
  action_label?: string;
};

export type VoiceUiHighlightData = {
  target: string;
  title?: string;
  body?: string;
  duration_ms?: number;
};

export type VoiceUiStartGuideData = {
  id: string;
  title: string;
  summary?: string;
  steps: Array<{
    id: string;
    say: string;
    title?: string | null;
    highlight?: string | null;
    href?: string | null;
    wait_for?: GuideWaitFor;
  }>;
};

export type VoiceUiActionPayload =
  | { action: 'navigate'; data: VoiceUiNavigateData }
  | { action: 'open_artifact'; data: UiOpenArtifactWire }
  | { action: 'open_workspace'; data: VoiceUiOpenWorkspaceData }
  | { action: 'workspace_update'; data: VoiceUiWorkspaceUpdateData }
  | { action: 'present_info'; data: VoiceUiPresentInfoData }
  | { action: 'toast'; data: VoiceUiToastData }
  | { action: 'highlight'; data: VoiceUiHighlightData }
  | { action: 'open_template_picker'; data?: Record<string, never> }
  | { action: 'start_guide'; data: VoiceUiStartGuideData };

const toGuideSteps = (raw: VoiceUiStartGuideData['steps']): GuideStep[] =>
  raw
    .filter((step) => typeof step?.id === 'string' && typeof step?.say === 'string')
    .map((step) => ({
      id: step.id,
      say: step.say,
      title: step.title ?? undefined,
      highlight: step.highlight ?? undefined,
      href: step.href ?? undefined,
      wait_for: step.wait_for ?? 'either',
    }));

const INTERNAL_BASE = 'https://__internal__';
const CONTROL_OR_WHITESPACE_RE = /[\x00-\x1f\s]/;

const isInternalPath = (href: string): boolean => {
  if (!href.startsWith('/') || href.startsWith('//')) return false;
  if (CONTROL_OR_WHITESPACE_RE.test(href)) return false;
  if (href.includes('\\')) return false;
  try {
    return new URL(href, INTERNAL_BASE).origin === INTERNAL_BASE;
  } catch {
    return false;
  }
};

/** Session id from `/scribe/session(s)/{id}` hrefs, else undefined. */
const scribeSessionIdFromHref = (href: string): string | undefined => {
  try {
    const { pathname } = new URL(href, INTERNAL_BASE);
    const match = /^\/scribe\/sessions?\/([^/]+)/.exec(pathname);
    if (!match?.[1]) return undefined;
    return decodeURIComponent(match[1]);
  } catch {
    return undefined;
  }
};

const VARIANT_TO_TONE: Record<'success' | 'error' | 'info', ToastTone> = {
  success: 'success',
  error: 'destructive',
  info: 'subtle',
};

const isHeidiWorkRoute = (pathname: string | undefined): boolean =>
  !!pathname?.startsWith('/scribe/chat/projects');

// A preview opens on the surface on screen, so an open sent while voice's own navigate is
// still running would land on the page being left. It waits for that navigation instead.
let pendingNavigation: Promise<void> | null = null;

/**
 * Apply a voice-channel UI action. Same outcomes as the agent-event bus
 * ``ui.navigate`` / ``ui.open_artifact`` / ``ui.toast`` / ``ui.highlight`` /
 * ``ui.open_template_picker`` handlers, plus voice-only ``start_guide`` playbooks.
 * App-shell reads and writes (router, artifact mapping and cache refresh) cross
 * on `port`, bound once at the app's voice UI-actions host.
 */
export const applyUiAction = (
  payload: VoiceUiActionPayload,
  port: VoiceUiActionsPort
): boolean => {
  switch (payload.action) {
    case 'navigate': {
      const href = payload.data.href?.trim() ?? '';
      if (!isInternalPath(href)) {
        // Don't log raw href — production warn path is unredacted.
        logger.warn('voice: ui.navigate rejected non-internal href');
        return false;
      }
      // Without BE pass-through for tasks/codes, classic Scribe still gets
      // navigate → /scribe/sessions/{id}. Pushing that while already on the
      // session remounts the page. Skip the push; opening the panel still
      // requires an open_artifact frame (not a bare navigate).
      const targetSessionId = scribeSessionIdFromHref(href);
      const currentSessionId = getCurrentSessionId()?.trim();
      if (targetSessionId && currentSessionId && targetSessionId === currentSessionId) {
        logger.info('voice: ui.navigate skipped — already on that session');
        return true;
      }
      const navigation = Promise.resolve(port.onNavigate(href)).finally(() => {
        if (pendingNavigation === navigation) pendingNavigation = null;
      });
      pendingNavigation = navigation;
      return true;
    }
    case 'open_artifact': {
      if (pendingNavigation) {
        // A navigation that fails drops the open, so it can't paint the page the user stayed on.
        void pendingNavigation.then(
          () => applyUiAction(payload, port),
          () => undefined
        );
        return true;
      }
      // Invalidate the react-query caches behind the artifact on BOTH
      // surfaces, before any navigation/focus. Voice tools do not ride the
      // agent-event bus that normally refreshes the session view, and
      // ScribeTabs builds the per-document tabs from the session's document
      // list — a just-created document only appears once that list refetches,
      // so skipping this on classic Scribe left the new tab invisible until a
      // full page refresh.
      const artifact = port.mapOpenArtifact(payload.data);
      if (artifact) {
        port.refreshArtifact(artifact);
      }
      // On classic Scribe, navigate / focus / open side panels — never fall
      // through to the Work canvas (type=patient used to wipe Talk context).
      if (!isHeidiWorkRoute(port.currentPathname())) {
        return applyClassicScribeOpenArtifact(payload.data, port);
      }
      if (!artifact) {
        logger.warn('voice: ui.open_artifact could not map payload', {
          type: payload.data.type,
        });
        return false;
      }
      if (!port.isWorkPreviewArtifact(artifact)) {
        logger.info('voice: ui.open_artifact type does not open a Work preview', {
          type: artifact.type,
        });
        return false;
      }
      getDefaultStore().set(setOpenArtifactSilentAtom, artifact);
      return true;
    }
    // Workspace frames (draft canvas + info feed) — see applyWorkspaceFrames.
    case 'open_workspace':
      return applyOpenWorkspaceFrame(payload.data);
    case 'workspace_update':
      return applyWorkspaceUpdateFrame(payload.data);
    case 'present_info':
      return applyPresentInfoFrame(payload.data);
    case 'toast': {
      const { variant, title, body, duration_ms, action_label } = payload.data;
      if (!title && !body) {
        logger.warn('voice: ui.toast requires title or body');
        return false;
      }
      let toastId: string | number | undefined;
      // eslint-disable-next-line prefer-const -- temporal-split assignment; id assigned by toast()
      toastId = toast({
        tone: VARIANT_TO_TONE[variant],
        icon: variant === 'info' ? <Info /> : undefined,
        title: title ?? body,
        description: title ? body : undefined,
        duration: duration_ms ?? 4000,
        action: action_label ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (toastId !== undefined) sonnerToast.dismiss(toastId);
            }}
          >
            {action_label}
          </Button>
        ) : undefined,
      });
      return true;
    }
    case 'highlight': {
      const target = payload.data.target?.trim() ?? '';
      if (!isHelpTargetId(target)) {
        logger.warn('voice: ui.highlight rejected unknown target');
        return false;
      }
      const title = payload.data.title?.trim().slice(0, 120) || undefined;
      const body = payload.data.body?.trim().slice(0, 500) || undefined;
      const durationMs = Math.min(
        30_000,
        Math.max(1500, payload.data.duration_ms ?? 8000)
      );
      useVoiceHighlightStore.getState().setHighlight({
        target,
        title,
        body,
        durationMs,
        startedAt: Date.now(),
      });
      return true;
    }
    case 'open_template_picker': {
      const ok = openTemplatePicker(port);
      if (!ok) {
        logger.warn('voice: ui.open_template_picker could not open picker');
      }
      return ok;
    }
    case 'start_guide': {
      const id = payload.data.id?.trim() ?? '';
      const title = payload.data.title?.trim() ?? '';
      const steps = toGuideSteps(payload.data.steps ?? []);
      if (!id || !title || steps.length === 0) {
        logger.warn('voice: ui.start_guide rejected incomplete playbook');
        return false;
      }
      useVoiceGuideStore.getState().startGuide({
        id,
        title,
        summary: payload.data.summary,
        steps,
        stepIndex: 0,
      });
      // useVoiceGuideRunner applies step visuals when the guide store updates.
      return true;
    }
    default: {
      const _exhaustive: never = payload;
      return _exhaustive;
    }
  }
};
